import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CalendarDays, Plus, Clock, MapPin, CalendarClock } from "lucide-react";
import { toast } from "sonner";

export default function ProAgenda() {
  const nav = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [patients, setPatients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ patient_id: "", scheduled_at: "", duration: 50, location: "Videochamada" });

  const load = () => api.get("/sessions").then((r) => setSessions(r.data));
  useEffect(() => { load(); api.get("/patients").then((r) => setPatients(r.data)); }, []);

  const pmap = Object.fromEntries(patients.map((p) => [p.id, p.name]));

  const create = async () => {
    if (!form.patient_id || !form.scheduled_at) { toast.error("Selecione paciente e data/hora."); return; }
    try {
      await api.post("/sessions", { ...form, scheduled_at: new Date(form.scheduled_at).toISOString(), duration: Number(form.duration) });
      toast.success("Sessão agendada."); setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openReschedule = (patientId) => { setForm((f) => ({ ...f, patient_id: patientId })); setOpen(true); };

  return (
    <div className="max-w-4xl mx-auto space-y-5 animate-fade-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#1F3B4D] flex items-center gap-2"><CalendarDays className="text-[#0F766E]" /> Agenda</h1>
          <p className="text-sm text-[#94A3B8]">Sessões, contatos e preparação.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="new-session-button" className="rounded-full bg-[#0F766E] hover:bg-[#0d6259] gap-2"><Plus size={16} /> Agendar</Button>
      </div>

      <div className="space-y-3">
        {sessions.map((s) => (
          <Card key={s.id} data-testid={`pro-session-${s.id}`} className="rounded-2xl p-4 border-[#E0F2FE] card-hover flex items-start justify-between gap-4">
            <div className="flex items-start gap-4 min-w-0">
              <div className="w-11 h-11 rounded-xl bg-[#0F766E]/10 grid place-items-center shrink-0"><CalendarClock className="text-[#0F766E]" /></div>
              <div className="min-w-0">
                <div className="font-semibold text-[#1F3B4D]">{pmap[s.patient_id] || "Paciente"}</div>
                <div className="text-xs text-[#94A3B8] flex gap-3 mt-0.5 flex-wrap">
                  <span className="capitalize">{new Date(s.scheduled_at).toLocaleString("pt-BR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                  <span className="flex items-center gap-1"><Clock size={12} /> {s.duration}min</span>
                  <span className="flex items-center gap-1"><MapPin size={12} /> {s.location}</span>
                </div>
                {s.status === "reschedule_requested" && <Badge className="bg-[#F59E0B]/15 text-[#b26a00] border-0 mt-2">Reagendamento solicitado</Badge>}
              </div>
            </div>
            <div className="flex flex-col items-stretch gap-2 shrink-0 w-32">
              {s.status === "reschedule_requested" && (
                <Button size="sm" variant="outline" className="rounded-full border-[#F59E0B] text-[#b26a00] hover:bg-[#F59E0B]/10" data-testid={`reschedule-${s.id}`} onClick={() => openReschedule(s.patient_id)}>Reagendar</Button>
              )}
              <Button size="sm" variant="outline" className="rounded-full border-[#E0F2FE]" data-testid={`prepare-${s.id}`} onClick={() => nav(`/pacientes/${s.patient_id}`)}>Preparar</Button>
            </div>
          </Card>
        ))}
        {sessions.length === 0 && <p className="text-sm text-[#94A3B8]">Nenhuma sessão agendada.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-heading text-[#1F3B4D]">Agendar sessão</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={form.patient_id} onValueChange={(v) => setForm({ ...form, patient_id: v })}>
              <SelectTrigger className="rounded-xl border-[#E0F2FE]" data-testid="session-patient-select"><SelectValue placeholder="Paciente" /></SelectTrigger>
              <SelectContent>{patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} data-testid="session-datetime-input" className="rounded-xl border-[#E0F2FE]" />
            <div className="flex gap-3">
              <Input type="number" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} className="rounded-xl border-[#E0F2FE]" placeholder="Duração (min)" />
              <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="rounded-xl border-[#E0F2FE]" placeholder="Local" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={create} data-testid="confirm-session-button" className="rounded-full bg-[#0F766E] hover:bg-[#0d6259]">Agendar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
